import React, { useRef, useCallback, useEffect } from "react";
import { createButtonPressAnimation } from "../../../../utilities";
import { Sprite } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toggleGlobalSound } from "../../../../State/actions";
import { selectHoldAndWinActive } from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { USER_INTERFACE_BUTTON_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";

const ButtonSound = ({ toolbar, zIndex = 0 }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { globalSoundDisabled } = useSelector((state) => state.sound);
  const isInHoldAndWin = useSelector(selectHoldAndWinActive);
  const { getResponsiveConfig, device, orientation } = useDimensions();

  // Add ref for the sprite to animate
  const buttonSpriteRef = useRef(null);

  // Create brightness filter for hover effect
  const brightnessFilter = useRef(new PIXI.filters.ColorMatrixFilter());
  brightnessFilter.current.brightness(2.0, false); // 30% brighter

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Determine which icon to show
  const iconName = globalSoundDisabled
    ? "icon_sound_disable"
    : "icon_sound_enable";
  const texture = PIXI.Assets.get(ASSET_NAME[iconName]);

  // Get responsive configuration from context
  const buttonConfig = getResponsiveConfig(USER_INTERFACE_BUTTON_CONFIG, true);

  // Calculate responsive button position based on device and orientation
  const buttonPosition = (() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width - buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8,
        };
      } else {
        return {
          x: buttonConfig.width * 0.8,
          y: buttonConfig.height * 0.8,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: toolbar.width - buttonConfig.width * 0.8,
          y: buttonConfig.width * 0.8,
        };
      } else {
        return {
          x: toolbar.width - buttonConfig.width * 0.8,
          y: buttonConfig.width * 0.8,
        };
      }
    } else {
      // desktop

      return {
        x: toolbar.width - buttonConfig.width,
        y: buttonConfig.width * 0.8,
      };
    }
  })();

  const handleClick = () => {
    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    soundManager.play(SOUND_NAME.button_click);

    // Add press animation before toggling sound
    if (buttonSpriteRef.current) {
      createButtonPressAnimation(buttonSpriteRef.current);
    }

    // Pass game state to determine which music to play
    dispatch(toggleGlobalSound({ isInHoldAndWin }));

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter.current];
      }
    }
  }, [buttonSpriteRef, brightnessFilter]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Sprite
      ref={buttonSpriteRef}
      texture={texture}
      name="secondary_button_sound"
      anchor={0.5}
      width={buttonConfig.width}
      height={buttonConfig.height}
      x={buttonPosition.x}
      y={buttonPosition.y}
      zIndex={zIndex}
      hitArea={new PIXI.Circle(0, 0, (buttonConfig.width / 2) * 1.8)}
      eventMode="static"
      cursor="pointer"
      onpointertap={handleClick}
      onpointerover={handlePointerOver}
      onpointerout={handlePointerOut}
      accessibleTitle={
        globalSoundDisabled ? t("enable_sound") : t("disable_sound")
      }
      accessibleHint={
        globalSoundDisabled ? t("enable_sound") : t("disable_sound")
      }
      resolution={2}
    />
  );
};

export default ButtonSound;
